import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { string, oneOf, func } from 'prop-types';
import { Overlay } from '@thd-olt-component-react/core-ui';
import { DualPathTooltip } from './DualPathTooltip';
import { ProductAddOnsPod } from '../../common/product-add-ons-pod';
import { useTippyToggle } from '../../../hooks/useTippyToggle';
import { dualPathContactInformation } from '../../../utils/product-add-on-constants';
import styles from './dual-path.scss';

const DualPath = ({
  channel,
  itemId,
  onChange,
  category,
  serviceUrl
}) => {
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const tooltipButtonRef = useTippyToggle(checked);
  const techshedUrl = `${serviceUrl}/?${itemId}&pageType=PIP#`;

  let cx = classNames.bind(styles);

  const actionButtonClasses = cx(
    'bttn bttn--atc buybox__add-on__request-a-quote',
    { 'u--hide': !checked }
  );

  const actionButtonWrapperClasses = cx(
    'dual-path__action-button',
    { 'u--hide': !checked }
  );

  const handleChange = (evt) => {
    LIFE_CYCLE_EVENT_BUS.trigger('dual-path.change', { isOptedIn: !checked, itemId });
    onChange({
      itemId,
      checked: evt.target.checked,
      addOnType: evt.target.value
    });
    setChecked(!checked);
  };

  const triggerAnalyticsEvent = () => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('dualPath.request-a-quote-action', { programCategory: category });
  };

  const requestAQuote = () => {
    setOpen(true);
    triggerAnalyticsEvent();
  };

  // eslint-disable-next-line react/prop-types
  const ContactInformation = ({ dualPathType = null }) => {
    if (!dualPathContactInformation[dualPathType]) {
      return null;
    }
    const tel = dualPathContactInformation[dualPathType]?.tel;
    return (
      <div className="dual-path__install-text">Or call
        <a className="u__default-link" href={`tel:${tel}`}>{dualPathContactInformation[dualPathType].label}</a>
      </div>
    );
  };

  const subHeading = useMemo(() => {
    if (!checked && ['brio_inaisle_sfi_twht', 'brio_inaisle_sfi_whtr'].includes(category)) {
      return (
        <div>Need help fast? Call&nbsp;
          <a className="u__default-link" href={`tel:${dualPathContactInformation[category].tel}`}>
            {dualPathContactInformation[category].label}
          </a>
        </div>
      );
    }

    return <div>A local pro will take care of the job for you.</div>;
  }, [checked, category]);

  return (
    <>
      <ProductAddOnsPod
        channel={channel}
        heading="Get Expert In-Home Installation"
        subHeading={subHeading}
        icon="use-and-care-manual.svg"
        iconHeight={36}
        iconWidth={36}
        onChange={handleChange}
        tooltip={(
          <DualPathTooltip
            channel={channel}
            category={category}
            ref={tooltipButtonRef}
          />
        )}
        userOptions={[
          {
            id: 'item ID',
            value: 'dual-path',
            checked,
            label: 'Add In-Home Installation'
          }
        ]}
      >
        <div className={actionButtonWrapperClasses}>
          <button type="button" className={actionButtonClasses} onClick={requestAQuote}>
            <span className="bttn__content">Request a Quote</span>
          </button>
          <ContactInformation dualPathType={category} />
        </div>
      </ProductAddOnsPod>
      <Overlay onClose={() => setOpen(false)} open={open} medium closeButton>
        <iframe title="Request A Quote" src={techshedUrl} width="100%" height="99%" />
      </Overlay>
    </>
  );
};

DualPath.displayName = 'ProductAddOns-DualPath';

DualPath.propTypes = {
  /** What view to render ['desktop' || 'mobile']  */
  channel: oneOf(['desktop', 'mobile']),
  itemId: string.isRequired,
  onChange: func.isRequired,
  serviceUrl: string.isRequired,
  category: string
};

DualPath.defaultProps = {
  channel: 'desktop',
  category: 'brio_inaisle_sfi_generalfs',
};

export { DualPath };